import React, { createContext, useContext, useEffect, useMemo } from 'react';

import type { ShellProps } from '../Shell/Shell';
import type { ShellContentProps } from '../Shell/ShellContent';
import type { ShellFooterProps } from '../Shell/ShellFooter';
import type { ShellHeaderProps } from '../Shell/ShellHeader';
import { atContainers, containers } from '../../common/containers';
import { shadows } from '../../common/shadows';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import { Shell } from '../Shell/Shell';
import { ShellContent } from '../Shell/ShellContent';
import { ShellFooter } from '../Shell/ShellFooter';
import { ShellHeader } from '../Shell/ShellHeader';

export type DrawerPreset = 'initial' | 'wide' | 'extra-wide';

/**
 * Descriptor for drawer width used by drawer-aware Pane styling. Mirrors Drawer's
 * preset and width/minWidth/maxWidth so padding can match the actual drawer size.
 */
export type DrawerWidthDescriptor = {
  preset?: DrawerPreset;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
};

export type DrawerWidthContextValue = {
  descriptor: DrawerWidthDescriptor | null;
  setDescriptor: (descriptor: DrawerWidthDescriptor | null) => void;
};

const noop = () => {};

export const DrawerWidthContext = createContext<DrawerWidthContextValue>({
  descriptor: null,
  setDescriptor: noop,
});

export function useDrawerWidthContext(): DrawerWidthContextValue {
  return useContext(DrawerWidthContext);
}

const DrawerHeaderContainer = styled(ShellHeader);
DrawerHeaderContainer.displayName = 'DrawerHeaderContainer';

export function DrawerHeader(props: ShellHeaderProps) {
  return <DrawerHeaderContainer type="drawer" {...props} />;
}

export type DrawerContentProps = ShellContentProps & { ref?: React.Ref<HTMLDivElement | null> };

export const DrawerContentContainer = styled(ShellContent);
DrawerContentContainer.displayName = 'DrawerContentContainer';

export function DrawerContent({ children, ref, ...remaining }: DrawerContentProps) {
  return (
    <DrawerContentContainer data-drawer-content ref={ref} type="drawer" {...remaining}>
      {children}
    </DrawerContentContainer>
  );
}

const DrawerFooterContainer = styled(ShellFooter);
DrawerFooterContainer.displayName = 'DrawerFooterContainer';

export type DrawerFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement | null>;
};

export function DrawerFooter({ actions, secondaryActions, ref, ...remaining }: DrawerFooterProps) {
  return (
    <DrawerFooterContainer
      ref={ref}
      start={secondaryActions}
      end={actions}
      type="drawer"
      {...remaining}
    />
  );
}

// Guarantees a minimum internal width for the Drawer.
export const drawerWidth = sizing.primary * 2 + 320;

const drawerWidthRem = `${drawerWidth / 16}rem`;

function toPaddingValue(value: number | string): string {
  if (typeof value === 'number') {
    return `${value}px`;
  }
  return value;
}

const PRESET_PADDING: Record<DrawerPreset, string> = {
  initial: drawerWidthRem,
  wide: '40vw',
  'extra-wide': '80vw',
};

/** All `DrawerPreset` values, derived from `PRESET_PADDING` keys so the union and padding map stay aligned. */
export const DRAWER_PRESET_VALUES = Object.keys(PRESET_PADDING) as DrawerPreset[];

/**
 * Returns the CSS value for drawer-matched padding on the pane (rem / px / vw).
 * Viewport gating belongs in CSS: wrap usage in Stitches `@notDesktop` / `@desktop`
 * so non-desktop layouts get `padding-right: 0` (full-screen drawer) without
 * coupling to {@link useViewport}.
 */
export function getDrawerPaddingValue(descriptor: DrawerWidthDescriptor | true): string {
  if (descriptor === true) {
    return drawerWidthRem;
  }
  if (descriptor.width !== undefined && descriptor.width !== null) {
    return toPaddingValue(descriptor.width);
  }
  if (descriptor.preset !== undefined) {
    return PRESET_PADDING[descriptor.preset];
  }
  if (descriptor.minWidth !== undefined && descriptor.minWidth !== null) {
    return toPaddingValue(descriptor.minWidth);
  }
  if (descriptor.maxWidth !== undefined && descriptor.maxWidth !== null) {
    return toPaddingValue(descriptor.maxWidth);
  }
  return drawerWidthRem;
}

export const DrawerContainer = styled(Shell, {
  position: 'relative',
  zIndex: zIndexes.drawer,
  gridArea: 'drawer',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,
  strokeAll: colors.strokeNeutralLight,
  pointerEvents: 'auto',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    strokeAll: colors.strokeNeutralDark,
  },

  '@desktop': {
    ...atContainers([containers.stage.narrow], {
      boxShadow: shadows.overlayAppLight,
      borderRadius: '$12',

      [darkThemeSelector]: {
        boxShadow: shadows.overlayAppDark,
      },
    }),
  },

  variants: {
    preset: {
      initial: {
        '@desktop': {
          width: `$${drawerWidth}`,
          minWidth: `$${drawerWidth}`,
        },
      },
      wide: {
        '@desktop': {
          width: `40vw`,
          minWidth: `40vw`,
        },
      },
      'extra-wide': {
        '@desktop': {
          width: `80vw`,
          minWidth: `80vw`,
        },
      },
    },
  },
});
DrawerContainer.displayName = 'DrawerContainer';

export type DrawerProps = Omit<ShellProps, 'type'> & {
  preset?: DrawerPreset;
};

function buildDescriptorFromProps(props: {
  preset?: DrawerPreset;
  width?: DrawerProps['width'];
  minWidth?: DrawerProps['minWidth'];
  maxWidth?: DrawerProps['maxWidth'];
}): DrawerWidthDescriptor {
  const { preset, width, minWidth, maxWidth } = props;
  const descriptor: DrawerWidthDescriptor = {};
  if (preset !== undefined) descriptor.preset = preset;
  if (width !== undefined && width !== null) descriptor.width = width;
  if (minWidth !== undefined && minWidth !== null) descriptor.minWidth = minWidth;
  if (maxWidth !== undefined && maxWidth !== null) descriptor.maxWidth = maxWidth;
  return descriptor;
}

export function Drawer({
  children,
  contentMode = 'fit',
  preset = 'initial',
  width,
  maxWidth,
  minWidth,
  ref,
  ...remaining
}: DrawerProps) {
  const { breakpoint } = useViewport();
  const { setDescriptor } = useDrawerWidthContext();
  const descriptor = useMemo(
    () => buildDescriptorFromProps({ preset, width, minWidth, maxWidth }),
    [preset, width, minWidth, maxWidth],
  );

  useEffect(() => {
    setDescriptor(descriptor);
    return () => setDescriptor(null);
  }, [descriptor, setDescriptor]);

  return (
    <DrawerContainer
      ref={ref}
      contentMode={contentMode}
      preset={preset}
      width={breakpoint !== 'desktop' ? '100%' : width}
      maxWidth={breakpoint !== 'desktop' ? '100%' : maxWidth}
      minWidth={breakpoint !== 'desktop' ? '100%' : minWidth}
      {...remaining}
      type="drawer"
    >
      {children}
    </DrawerContainer>
  );
}
